import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import { toIsoDate } from '@/api/reportDateRanges';
import { BudgetUsageTable } from '@/components/dashboard/BudgetUsageTable';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
  dashboardDateRangeToStreamTime,
  formatBudgetUsageDateTime,
  formatUsagePercent,
  latestBudgetUsageEvent,
  usageRiskLevel,
} from '@/utils/budgetUsage';
import type { BudgetUsageEvent } from '@/types/budgetUsage';

const ENTITY_BUDGET_USAGE_LIMIT = 100;
const ENTITY_LOOKBACK_DAYS = 30;
const TODAY_BUDGET_USAGE_LIMIT = 50;

type BudgetUsageEntityPanelProps = {
  profileId: string;
  campaignId?: string;
  portfolioId?: string;
  entityLabel: string;
  /** `todayLatest` shows only today's newest alert and hides when none exist. */
  display?: 'table' | 'todayLatest';
};

function defaultEntityDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - ENTITY_LOOKBACK_DAYS);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function todayDateRange(): { startDate: string; endDate: string } {
  const today = toIsoDate(new Date());
  return { startDate: today, endDate: today };
}

function UsagePercentBadge({ percent }: { percent: number | null }) {
  if (percent == null || !Number.isFinite(percent)) {
    return <span className="text-foreground-subtle">—</span>;
  }
  const risk = usageRiskLevel(percent);
  if (risk === 'high') {
    return (
      <span className="inline-flex items-center rounded border border-error/20 bg-error-subtle px-2 py-0.5 text-xs font-medium text-error-ink">
        {formatUsagePercent(percent)}
      </span>
    );
  }
  const variant: Record<'low' | 'medium', 'success' | 'warning'> = {
    low: 'success',
    medium: 'warning',
  };
  return <Badge variant={variant[risk]}>{formatUsagePercent(percent)}</Badge>;
}

function BudgetUsageTodayFields({ event }: { event: BudgetUsageEvent }) {
  return (
    <div>
      <div className="text-foreground-subtle">Budget usage (today)</div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <UsagePercentBadge percent={event.budgetUsagePercent} />
        <span className="text-foreground-subtle">
          · {formatBudgetUsageDateTime(event.usageUpdatedAt)}
        </span>
      </div>
    </div>
  );
}

export function BudgetUsageEntityPanel({
  profileId,
  campaignId,
  portfolioId,
  entityLabel,
  display = 'table',
}: BudgetUsageEntityPanelProps) {
  const range = useMemo(
    () => (display === 'todayLatest' ? todayDateRange() : defaultEntityDateRange()),
    [display]
  );
  const streamTime = useMemo(
    () => dashboardDateRangeToStreamTime(range.startDate, range.endDate),
    [range]
  );
  const limit = display === 'todayLatest' ? TODAY_BUDGET_USAGE_LIMIT : ENTITY_BUDGET_USAGE_LIMIT;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'stream-budget-usage-entity',
      profileId,
      campaignId ?? null,
      portfolioId ?? null,
      streamTime.startTime,
      streamTime.endTime,
      display,
    ],
    queryFn: async () => {
      const res = await adsApi.getStreamBudgetUsage(profileId, {
        startTime: streamTime.startTime,
        endTime: streamTime.endTime,
        limit,
        campaignId,
        portfolioId,
        view: 'events',
      });
      if (!res.success || !res.data) throw new Error('Failed to load budget usage alerts');
      return res.data;
    },
    enabled: Boolean(profileId && (campaignId || portfolioId)),
    staleTime: 30_000,
  });

  const events = data?.rows ?? [];
  const latestTodayEvent = useMemo(
    () => (display === 'todayLatest' ? latestBudgetUsageEvent(events) : null),
    [display, events]
  );
  const campaignNames = useMemo(() => {
    const map = new Map<string, string>();
    if (campaignId) map.set(campaignId, entityLabel);
    return map;
  }, [campaignId, entityLabel]);
  const portfolioNames = useMemo(() => {
    const map = new Map<string, string>();
    if (portfolioId) map.set(portfolioId, entityLabel);
    return map;
  }, [portfolioId, entityLabel]);

  if (display === 'todayLatest') {
    if (isLoading || isError || !latestTodayEvent) return null;

    return <BudgetUsageTodayFields event={latestTodayEvent} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-foreground-subtle">
        <Spinner className="h-4 w-4" />
        Loading budget usage alerts…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-sm text-error">
        {error instanceof Error ? error.message : 'Failed to load budget usage alerts.'}
        <button type="button" onClick={() => void refetch()} className="ml-2 underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-foreground-subtle">
        Marketing Stream threshold notifications for {entityLabel} over the last{' '}
        {ENTITY_LOOKBACK_DAYS} days (~5% increments).
      </p>
      {events.length === 0 ? (
        <p className="text-sm text-foreground-subtle">
          No budget usage alerts recorded for this entity in the last {ENTITY_LOOKBACK_DAYS} days.
        </p>
      ) : (
        <BudgetUsageTable
          events={events}
          profileId={profileId}
          campaignNames={campaignNames}
          portfolioNames={portfolioNames}
          linkCampaigns={false}
          emptyMessage="No budget usage alerts for this entity."
        />
      )}
    </div>
  );
}
